<template>
  <header class="overview-header">
    <h1>Create Pay Stub</h1>
  </header>

  <!-- Employee Selection -->
  <section class="form-section">
    <div class="form-group">
      <label for="employeeSelect">Select Employee</label>
      <div class="employee-search-wrapper">
        <input
          v-model="employeeSearchTerm"
          type="text"
          placeholder="Search by name or employee ID..."
          @input="showEmployeeDropdown = employeeSearchTerm.length > 0"
          @focus="showEmployeeDropdown = employeeSearchTerm.length > 0"
          @blur="hideEmployeeDropdown"
        />
        <div v-if="showEmployeeDropdown" class="employee-dropdown">
          <div
            v-for="emp in filteredEmployees"
            :key="emp.id"
            class="employee-option"
            @mousedown="selectEmployee(emp)"
          >
            <div class="employee-name">{{ emp.firstName }} {{ emp.lastName }}</div>
            <div class="employee-details">ID: {{ emp.employeeId }} | {{ emp.position || 'No Position' }}</div>
          </div>
          <div v-if="filteredEmployees.length === 0" class="no-employees">
            No employees found
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Pay Stub Creation Form -->
  <section v-if="selectedEmployee" class="form-section">
    <h2>Create Pay Stub for {{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</h2>

    <form @submit.prevent="editingPayStubId ? updatePayStub() : createPayStub()">
      <!-- Pay Period Information -->
      <div class="form-subsection">
        <h3>Pay Period Information</h3>
        <div class="grid grid-3">
          <div class="form-group">
            <label for="payPeriodStart">Pay Period Start *</label>
            <input id="payPeriodStart" v-model="form.payPeriodStart" type="date" required />
          </div>
          <div class="form-group">
            <label for="payPeriodEnd">Pay Period End *</label>
            <input id="payPeriodEnd" v-model="form.payPeriodEnd" type="date" required />
          </div>
          <div class="form-group">
            <label for="payDate">Pay Date *</label>
            <input id="payDate" v-model="form.payDate" type="date" required />
          </div>
        </div>
      </div>

      <!-- Invoice Selection -->
      <div class="form-subsection">
        <h3>Invoice Selection</h3>
        <div class="form-group">
          <label for="invoiceSearch">Invoice *</label>
          <div class="employee-search-wrapper">
            <input
              id="invoiceSearch"
              v-model="invoiceSearchTerm"
              type="text"
              autocomplete="off"
              placeholder="Type invoice # or property, or click to see recent invoices..."
              @input="onInvoiceSearchInput"
              @focus="openInvoiceDropdown"
              @blur="hideInvoiceDropdown"
              @keydown.down.prevent="moveInvoiceHighlight(1)"
              @keydown.up.prevent="moveInvoiceHighlight(-1)"
              @keydown.enter.prevent="selectHighlightedInvoice"
              @keydown.esc="showInvoiceDropdown = false"
            />
            <div v-if="showInvoiceDropdown" class="employee-dropdown">
              <div
                v-for="(invoice, idx) in invoiceMatches"
                :key="invoice.id"
                :class="['employee-option', { highlighted: idx === invoiceHighlight }]"
                @mousedown="selectInvoice(invoice)"
              >
                <div class="employee-name">
                  Invoice #{{ invoice.invoiceNumber }}
                  <span v-if="paidInvoiceIds.has(invoice.id)" class="paid-tag">Paid</span>
                </div>
                <div class="employee-details">
                  {{ formatDate(invoice.invoiceDate) }} | {{ invoice.propertyName || 'No property' }} | {{ invoice.departmentName || '—' }}
                </div>
              </div>
              <div v-if="invoiceMatches.length === 0" class="no-employees">
                {{ invoiceNoMatchMessage }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedInvoice" class="invoice-details">
          <h4>Invoice Details</h4>
          <div class="grid grid-2">
            <div class="detail-item">
              <strong>Property:</strong> {{ selectedInvoice.propertyName }}
            </div>
            <div class="detail-item">
              <strong>Department:</strong> {{ selectedInvoice.departmentName }}
            </div>
            <div class="detail-item">
              <strong>Total Hours:</strong> {{ (selectedInvoice.totalHours || 0).toFixed(2) }}
            </div>
            <div class="detail-item">
              <strong>Employee Hourly Rate:</strong> {{ currency(currentEmployeeHourlyRate) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Memo -->
      <div class="form-subsection">
        <h3>Memo</h3>
        <div class="form-group">
          <label for="memo">Additional Notes (Optional)</label>
          <textarea
            id="memo"
            v-model="form.memo"
            placeholder="Add any additional notes or comments..."
            style="min-height: 80px; resize: vertical;"
          ></textarea>
        </div>
      </div>

      <!-- Pay Stub Information -->
      <div class="form-subsection">
        <h3>Pay Stub Information</h3>
        <p class="info-note">
          Hourly rate is automatically determined from the employee's assigned property rates.
          Gross wages are calculated as: (Hourly Rate × Total Hours) + (Overtime Rate × Overtime Hours)
          Overtime rate is automatically set to 1.5× the hourly rate.
        </p>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="totalHours">Total Hours *</label>
            <input id="totalHours" v-model.number="form.totalHours" type="number" step="0.01" min="0" @input="calculateGrossWages" required />
          </div>
          <div class="form-group">
            <label for="overtimeHours">Overtime Hours</label>
            <input id="overtimeHours" v-model.number="form.overtimeHours" type="number" step="0.01" min="0" @input="calculateGrossWages" />
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="hourlyRate">Hourly Rate</label>
            <input id="hourlyRate" v-model.number="form.hourlyRate" type="number" step="0.01" min="0" readonly class="readonly-field" />
          </div>
          <div class="form-group">
            <label for="overtimeRate">Overtime Rate (1.5×)</label>
            <input id="overtimeRate" v-model.number="form.overtimeRate" type="number" step="0.01" min="0" readonly class="readonly-field" />
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="grossWages">Gross Wages</label>
            <input id="grossWages" v-model.number="form.grossWages" type="number" step="0.01" min="0" readonly class="readonly-field" />
          </div>
          <div class="form-group">
            <label for="netWages">Net Wages</label>
            <input id="netWages" v-model.number="form.netWages" type="number" step="0.01" min="0" readonly class="readonly-field" />
          </div>
        </div>
      </div>

      <!-- Deductions -->
      <div class="form-subsection">
        <h3>Deductions</h3>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="rent">Rent</label>
            <input id="rent" v-model.number="form.rent" type="number" step="0.01" min="0" @input="calculateNetWages" />
          </div>
          <div class="form-group">
            <label for="federalIncome">Federal Income Tax</label>
            <div class="tax-input-group">
              <select id="federalTaxMode" v-model="federalTaxMode" @change="calculateFederalTax" class="tax-mode-select">
                <option value="0">None ($0.00)</option>
                <option value="auto">8.4% of Gross</option>
              </select>
              <input id="federalIncome" v-model.number="form.federalIncome" type="number" step="0.01" min="0" readonly class="readonly-field tax-amount-display" />
            </div>
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="socialSecurity">Social Security</label>
            <div class="tax-input-group">
              <select id="socialSecurityMode" v-model="socialSecurityMode" @change="calculateSocialSecurity" class="tax-mode-select">
                <option value="0">None</option>
                <option value="auto">6.2% of Gross</option>
              </select>
              <input id="socialSecurity" v-model.number="form.socialSecurity" type="number" step="0.01" min="0" readonly class="readonly-field tax-amount-display" />
            </div>
          </div>
          <div class="form-group">
            <label for="medicare">Medicare</label>
            <div class="tax-input-group">
              <select id="medicareMode" v-model="medicareMode" @change="calculateMedicare" class="tax-mode-select">
                <option value="0">None</option>
                <option value="auto">1.45% of Gross</option>
              </select>
              <input id="medicare" v-model.number="form.medicare" type="number" step="0.01" min="0" readonly class="readonly-field tax-amount-display" />
            </div>
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="otherDeductions">Other Deductions</label>
            <input id="otherDeductions" v-model.number="form.otherDeductions" type="number" step="0.01" min="0" @input="calculateNetWages" />
          </div>
        </div>

        <div class="total-deductions">
          <strong>Total Deductions: {{ currency(totalDeductions) }}</strong>
        </div>
      </div>

      <!-- Additions -->
      <div class="form-subsection">
        <h3>Additions</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="refund">Refund / Reimbursement</label>
            <input id="refund" v-model.number="form.refund" type="number" step="0.01" min="0" @input="calculateNetWages" />
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="form-subsection summary-section">
        <h3>Summary</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">Gross Wages:</span>
            <span class="summary-value">{{ currency(form.grossWages) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Total Deductions:</span>
            <span class="summary-value">{{ currency(totalDeductions) }}</span>
          </div>
          <div class="summary-item final-pay">
            <span class="summary-label">Net Wages:</span>
            <span class="summary-value">{{ currency(form.netWages) }}</span>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="submit" class="save-button" :disabled="!selectedEmployee || !form.invoiceId">
          {{ editingPayStubId ? 'Update Pay Stub' : 'Create Pay Stub' }}
        </button>
        <button type="button" class="cancel-button" @click="clearForm">
          {{ editingPayStubId ? 'Cancel Edit' : 'Clear Form' }}
        </button>
      </div>
    </form>
  </section>

  <!-- Pay Stubs Table -->
  <section class="form-section">
    <div class="invoice-table-controls">
      <div class="table-search">
        <i class="fas fa-search search-icon"></i>
        <input v-model="payStubSearch" type="search" placeholder="Search by employee name or ID…"
          aria-label="Search pay stubs" />
      </div>

      <div class="year-filter">
        <label for="payStubYear">Year</label>
        <select id="payStubYear" v-model="selectedYear" :disabled="isSearchingPayStubs">
          <option v-for="y in payStubYears" :key="y" :value="y">{{ y }}</option>
          <option value="all">All years</option>
        </select>
      </div>

      <div class="archive-toggle">
        <label>
          <input type="checkbox" v-model="showArchivedPayStubs" :disabled="isSearchingPayStubs" />
          Show Archived Pay Stubs ({{ payStubs.filter(ps => ps.archived).length }})
        </label>
      </div>
    </div>

    <div v-if="isSearchingPayStubs" class="search-summary">
      <template v-if="singleEmployeeSummary">
        <strong>{{ singleEmployeeSummary.name }}</strong>
        <span v-if="singleEmployeeSummary.number">(ID: {{ singleEmployeeSummary.number }})</span>
        · {{ filteredPayStubs.length }} stub(s)
        · Gross {{ currency(singleEmployeeSummary.gross) }}
        · Net {{ currency(singleEmployeeSummary.net) }}
      </template>
      <template v-else>{{ filteredPayStubs.length }} matching stub(s)</template>
      <span class="search-scope">Searching all years, including archived</span>
    </div>

    <div v-if="selectedPayStubs.length > 0" class="bulk-actions">
      <span class="selection-count">{{ selectedPayStubs.length }} pay stub(s) selected</span>
      <button class="bulk-archive-button" @click="bulkArchivePayStubs">
        <i class="fas fa-archive"></i> Archive Selected
      </button>
      <button class="bulk-cancel-button" @click="clearSelection">Cancel</button>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th style="width: 40px;">
              <input type="checkbox" @change="toggleSelectAll" :checked="allPayStubsSelected" />
            </th>
            <th class="sortable" @click="togglePayStubSort('employee')">
              Employee {{ payStubSort === 'employee' ? '▲' : '' }}
            </th>
            <th class="sortable" @click="togglePayStubSort('payDate')">
              Pay Date {{ payStubSort === 'payDate' ? '▼' : '' }}
            </th>
            <th>Pay Period</th>
            <th>Property</th>
            <th>Gross Wages</th>
            <th>Net Wages</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="payStub in pagedPayStubs" :key="payStub.id">
            <td>
              <input type="checkbox" :checked="selectedPayStubs.includes(payStub.id)"
                @change="togglePayStubSelection(payStub.id)" />
            </td>
            <td>
              {{ payStub.employeeName }}
              <div v-if="stubEmployeeNumber(payStub)" class="employee-number">ID: {{ stubEmployeeNumber(payStub) }}</div>
              <span v-if="payStub.archived && isSearchingPayStubs" class="archived-tag">Archived</span>
            </td>
            <td>{{ formatDate(payStub.payDate) }}</td>
            <td>{{ formatDateRange(payStub.payPeriodStart, payStub.payPeriodEnd) }}</td>
            <td>{{ payStub.propertyName }}</td>
            <td class="num">{{ currency(payStub.grossWages) }}</td>
            <td class="num">{{ currency(payStub.netWages) }}</td>
            <td>{{ formatDate(payStub.createdAt) }}</td>
            <td>
              <button class="action-button view" @click="viewPayStub(payStub)">View</button>
              <button class="action-button print" @click="printPayStub(payStub)">Print</button>
              <button v-if="!payStub.archived" class="action-button archive" @click="archivePayStub(payStub.id)">Archive</button>
              <button v-else class="action-button unarchive" @click="unarchivePayStub(payStub.id)">Unarchive</button>
              <button class="action-button delete" @click="deletePayStub(payStub.id)">Delete</button>
            </td>
          </tr>
          <tr v-if="filteredPayStubs.length === 0">
            <td colspan="9" class="no-data">{{ emptyPayStubsMessage }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="filteredPayStubs.length > 0" class="pagination">
      <span>Showing {{ pageStart + 1 }}–{{ pageStart + pagedPayStubs.length }} of {{ filteredPayStubs.length }}</span>
      <div class="pagination-controls">
        <button type="button" :disabled="currentPage === 1" @click="currentPage = 1" aria-label="First page">«</button>
        <button type="button" :disabled="currentPage === 1" @click="currentPage--">Prev</button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button type="button" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
        <button type="button" :disabled="currentPage === totalPages" @click="currentPage = totalPages" aria-label="Last page">»</button>
        <select v-model.number="pageSize" aria-label="Rows per page">
          <option :value="25">25 / page</option>
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
        </select>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { db, auth } from '@/firebase'
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import html2pdf from 'html2pdf.js'
import { getYear, toTime, yearOptions } from '@/utils/dates'

const router = useRouter()

const employees = ref([])
const invoices = ref([])
const payStubs = ref([])
const selectedEmployee = ref(null)
const selectedInvoice = ref(null)
const employeeSearchTerm = ref('')
const showEmployeeDropdown = ref(false)
const federalTaxMode = ref('0')
const socialSecurityMode = ref('0')
const medicareMode = ref('0')
const editingPayStubId = ref(null)
const showArchivedPayStubs = ref(false)
const selectedPayStubs = ref([])
const selectedYear = ref(new Date().getFullYear())
const payStubSearch = ref('')
const payStubSort = ref('payDate') // 'payDate' (newest first) | 'employee' (A–Z, then newest first)
const currentPage = ref(1)
const pageSize = ref(25)

let unsubEmployees = null
let unsubInvoices = null
let unsubPayStubs = null

const form = reactive({
  payPeriodStart: '',
  payPeriodEnd: '',
  payDate: todayISO(),
  invoiceId: '',
  memo: '',
  grossWages: 0,
  totalHours: 0,
  hourlyRate: 0,
  overtimeHours: 0,
  overtimeRate: 0,
  rent: 0,
  federalIncome: 0,
  socialSecurity: 0,
  medicare: 0,
  refund: 0,
  otherDeductions: 0,
  netWages: 0,
  ytdGrossWages: 0,
  ytdNetWages: 0,
  ytdFederalIncome: 0,
  ytdSocialSecurity: 0,
  ytdMedicare: 0,
  ytdRent: 0
})

/** ---------------------------
 * Helpers (robust date + rounding)
 * -------------------------- */
function toDate(value) {
  if (!value) return null
  if (typeof value === 'object' && value.seconds !== undefined) return new Date(value.seconds * 1000) // Firestore Timestamp
  if (value instanceof Date) return value
  const d = new Date(value) // ISO string or other
  return Number.isNaN(d.getTime()) ? null : d
}
function round2(n) {
  return Number((Number(n || 0)).toFixed(2))
}

/** ---------- Computed ---------- */
const filteredEmployees = computed(() => {
  if (!employeeSearchTerm.value || employeeSearchTerm.value.length < 1) return []
  const term = employeeSearchTerm.value.toLowerCase()
  return employees.value
    .filter(emp => {
      const firstName = (emp.firstName || '').toLowerCase()
      const lastName = (emp.lastName || '').toLowerCase()
      const employeeId = (emp.employeeId || '').toLowerCase()
      const fullName = `${firstName} ${lastName}`.trim()
      return firstName.includes(term) || lastName.includes(term) || fullName.includes(term) || employeeId.includes(term)
    })
    .slice(0, 10)
})

const employeeInvoices = computed(() => {
  if (!selectedEmployee.value) return []
  return invoices.value.filter(invoice => {
    return invoice.lineItems?.some(
      item =>
        item.employee === selectedEmployee.value.id ||
        item.employeeId === selectedEmployee.value.employeeId ||
        item.employeeName === `${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName}`
    )
  })
})

const totalDeductions = computed(() => {
  return (
    Number(form.rent || 0) +
    Number(form.federalIncome || 0) +
    Number(form.socialSecurity || 0) +
    Number(form.medicare || 0) +
    Number(form.otherDeductions || 0)
  )
})

const currentEmployeeHourlyRate = computed(() => {
  if (!selectedEmployee.value || !selectedInvoice.value) return 0
  return getEmployeeHourlyRateForProperty(selectedEmployee.value, selectedInvoice.value.propertyId)
})

const allPayStubsSelected = computed(() =>
  pagedPayStubs.value.length > 0 && pagedPayStubs.value.every(ps => selectedPayStubs.value.includes(ps.id))
)

/** ---------- Invoice type-ahead ---------- */
const invoiceSearchTerm = ref('')
const showInvoiceDropdown = ref(false)
const invoiceHighlight = ref(0)

// Invoices this employee already has a pay stub for (ignoring the stub being edited)
const paidInvoiceIds = computed(() => {
  if (!selectedEmployee.value) return new Set()
  return new Set(
    payStubs.value
      .filter(ps => ps.employeeId === selectedEmployee.value.id && ps.id !== editingPayStubId.value)
      .map(ps => ps.invoiceId)
  )
})

// Employee's invoices: unpaid first, then newest first. Empty search shows the 10 most relevant.
const invoiceMatches = computed(() => {
  const term = invoiceSearchTerm.value.trim().toLowerCase()
  const paid = paidInvoiceIds.value
  const matches = employeeInvoices.value
    .filter(inv => !term || [inv.invoiceNumber, inv.propertyName]
      .some(f => String(f ?? '').toLowerCase().includes(term)))
    .sort((a, b) =>
      (paid.has(a.id) - paid.has(b.id)) || (toTime(b.invoiceDate) - toTime(a.invoiceDate)))
  return term ? matches.slice(0, 25) : matches.slice(0, 10)
})

const invoiceNoMatchMessage = computed(() => {
  const term = invoiceSearchTerm.value.trim().toLowerCase()
  if (!term) return 'No invoices include this employee.'
  const elsewhere = invoices.value.find(inv => String(inv.invoiceNumber ?? '').toLowerCase() === term)
  if (elsewhere && selectedEmployee.value) {
    return `Invoice #${elsewhere.invoiceNumber} doesn't include ${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName}.`
  }
  return 'No matching invoices for this employee.'
})

function invoiceLabel(inv) {
  return `#${inv.invoiceNumber} - ${inv.propertyName || ''}`.trim()
}

function openInvoiceDropdown() {
  invoiceHighlight.value = 0
  showInvoiceDropdown.value = true
}

function hideInvoiceDropdown() {
  setTimeout(() => (showInvoiceDropdown.value = false), 150)
}

function onInvoiceSearchInput() {
  // Editing the text drops the previous pick until a new invoice is chosen
  if (form.invoiceId) {
    form.invoiceId = ''
    selectedInvoice.value = null
  }
  openInvoiceDropdown()
}

function moveInvoiceHighlight(step) {
  if (!showInvoiceDropdown.value) return openInvoiceDropdown()
  const count = invoiceMatches.value.length
  if (count) invoiceHighlight.value = (invoiceHighlight.value + step + count) % count
}

function selectHighlightedInvoice() {
  const inv = invoiceMatches.value[invoiceHighlight.value]
  if (showInvoiceDropdown.value && inv) selectInvoice(inv)
}

function selectInvoice(inv) {
  form.invoiceId = inv.id
  showInvoiceDropdown.value = false
  onInvoiceChange()
}

const payStubYears = computed(() => yearOptions(payStubs.value, 'payDate'))

// Older stubs predate the stored employeeNumber, so fall back to the employee record
const employeeNumberById = computed(() =>
  new Map(employees.value.map(e => [e.id, e.employeeId || '']))
)

function stubEmployeeNumber(ps) {
  return ps.employeeNumber || employeeNumberById.value.get(ps.employeeId) || ''
}

const isSearchingPayStubs = computed(() => payStubSearch.value.trim().length > 0)

const filteredPayStubs = computed(() => {
  const term = payStubSearch.value.trim().toLowerCase()
  const byNewest = (a, b) => toTime(b.payDate) - toTime(a.payDate)
  // A search covers every year and archived stubs so all of a person's stubs come up together
  const rows = term
    ? payStubs.value.filter(ps => [ps.employeeName, stubEmployeeNumber(ps)]
      .some(f => String(f ?? '').toLowerCase().includes(term)))
    : payStubs.value
      .filter(ps => showArchivedPayStubs.value ? ps.archived === true : !ps.archived)
      .filter(ps => selectedYear.value === 'all' || getYear(ps.payDate) === selectedYear.value)
  return [...rows].sort(payStubSort.value === 'employee'
    ? (a, b) => String(a.employeeName ?? '').localeCompare(String(b.employeeName ?? '')) || byNewest(a, b)
    : byNewest)
})

// Totals line when a search narrows the list to one employee
const singleEmployeeSummary = computed(() => {
  const rows = filteredPayStubs.value
  if (!rows.length || new Set(rows.map(ps => ps.employeeId)).size !== 1) return null
  return {
    name: rows[0].employeeName,
    number: stubEmployeeNumber(rows[0]),
    gross: rows.reduce((s, ps) => s + (Number(ps.grossWages) || 0), 0),
    net: rows.reduce((s, ps) => s + (Number(ps.netWages) || 0), 0)
  }
})

const totalPages = computed(() => Math.max(1, Math.ceil(filteredPayStubs.value.length / pageSize.value)))
const pageStart = computed(() => (currentPage.value - 1) * pageSize.value)
const pagedPayStubs = computed(() =>
  filteredPayStubs.value.slice(pageStart.value, pageStart.value + pageSize.value)
)

// Stay on a valid page when rows are archived/deleted from under us
watch(totalPages, (pages) => {
  if (currentPage.value > pages) currentPage.value = pages
})

function togglePayStubSort(key) {
  payStubSort.value = key
}

const emptyPayStubsMessage = computed(() => {
  if (isSearchingPayStubs.value) return 'No pay stubs match that name or ID.'
  const scope = selectedYear.value === 'all' ? '' : ` for ${selectedYear.value}`
  return showArchivedPayStubs.value ? `No archived pay stubs${scope}.` : `No pay stubs${scope} yet.`
})

// On any filter change: back to page 1, and drop selections so bulk actions can't touch hidden rows
watch([selectedYear, payStubSearch, showArchivedPayStubs, payStubSort, pageSize], () => {
  currentPage.value = 1
  selectedPayStubs.value = []
})
watch(currentPage, () => {
  selectedPayStubs.value = []
})

/** ---------- Lifecycle ---------- */
onMounted(() => {
  const un = onAuthStateChanged(auth, (user) => {
    if (!user) {
      router.replace({ name: 'Login' })
    } else {
      unsubEmployees = onSnapshot(collection(db, 'employees'), (snap) => {
        employees.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      })

      unsubInvoices = onSnapshot(collection(db, 'invoices'), (snap) => {
        invoices.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      })

      unsubPayStubs = onSnapshot(collection(db, 'payStubs'), (snap) => {
        payStubs.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      })
    }
    un()
  })
})

onUnmounted(() => {
  if (unsubEmployees) unsubEmployees()
  if (unsubInvoices) unsubInvoices()
  if (unsubPayStubs) unsubPayStubs()
})

/** Recalculate YTD when payDate changes */
watch(
  () => form.payDate,
  () => {
    if (selectedEmployee.value) calculateYTD()
  }
)

/** Recalculate YTD when pay stubs list updates (new paystubs created elsewhere) */
watch(
  () => payStubs.value,
  () => {
    if (selectedEmployee.value) calculateYTD()
  }
)

/** Recalculate YTD when grossWages changes (from manual hour edits) */
watch(
  () => form.grossWages,
  () => {
    if (selectedEmployee.value) calculateYTD()
  }
)

/** ---------- UI Helpers ---------- */
function hideEmployeeDropdown() {
  // small delay so click can register
  setTimeout(() => (showEmployeeDropdown.value = false), 150)
}

/** ---------- Handlers ---------- */
function selectEmployee(employee) {
  selectedEmployee.value = employee
  employeeSearchTerm.value = `${employee.firstName} ${employee.lastName}`
  showEmployeeDropdown.value = false
  clearFormData()

  if (employee.rentAmount) form.rent = Number(employee.rentAmount)

  // Ensure YTD baseline recalcs once employee selected
  calculateYTD()
}

function getEmployeeHourlyRateForProperty(employee, propertyId) {
  if (!employee || !employee.assignedProperties || !propertyId) return 0
  const assignedProperty = employee.assignedProperties.find(prop => prop.propertyId === propertyId)
  return assignedProperty ? Number(assignedProperty.hourlyRate || 0) : 0
}

function onInvoiceChange() {
  selectedInvoice.value = invoices.value.find(inv => inv.id === form.invoiceId)
  if (selectedInvoice.value) invoiceSearchTerm.value = invoiceLabel(selectedInvoice.value)
  if (!selectedInvoice.value || !selectedEmployee.value) return

  const employeeLineItem = selectedInvoice.value.lineItems?.find(
    item =>
      item.employee === selectedEmployee.value.id ||
      item.employeeId === selectedEmployee.value.employeeId ||
      item.employeeName === `${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName}`
  )

  if (employeeLineItem) {
    form.totalHours = Number(employeeLineItem.hours || 0)
    form.overtimeHours = Number(employeeLineItem.overtimeHours || 0)
    form.hourlyRate = getEmployeeHourlyRateForProperty(selectedEmployee.value, selectedInvoice.value.propertyId)
    calculateOvertimeRate()
    // calculateOvertimeRate calls calculateGrossWages which calls calculateFederalTax, 
    // calculateSocialSecurity, calculateMedicare, and calculateNetWages
    // Then explicitly recalculate YTD with complete form data
    calculateYTD()
  }
}

function calculateGrossWages() {
  const regularPay = Number(form.hourlyRate || 0) * Number(form.totalHours || 0)
  const overtimePay = Number(form.overtimeRate || 0) * Number(form.overtimeHours || 0)
  form.grossWages = round2(regularPay + overtimePay)

  calculateFederalTax()
  calculateSocialSecurity()
  calculateMedicare()
  calculateNetWages()
}

function calculateOvertimeRate() {
  form.overtimeRate = round2(Number(form.hourlyRate || 0) * 1.5)
  calculateGrossWages()
}

function calculateFederalTax() {
  form.federalIncome = federalTaxMode.value === 'auto' ? round2(Number(form.grossWages || 0) * 0.084) : 0
  calculateNetWages()
}

function calculateSocialSecurity() {
  form.socialSecurity = socialSecurityMode.value === 'auto' ? round2(Number(form.grossWages || 0) * 0.062) : 0
  calculateNetWages()
}

function calculateMedicare() {
  form.medicare = medicareMode.value === 'auto' ? round2(Number(form.grossWages || 0) * 0.0145) : 0
  calculateNetWages()
}

function calculateNetWages() {
  form.netWages = round2(Number(form.grossWages || 0) - totalDeductions.value + Number(form.refund || 0))
  calculateYTD()
}

/**
 * ✅ Correct YTD:
 * - same employee (doc id OR employee number)
 * - same year
 * - only stubs with payDate < current payDate (so no future stubs)
 * - add CURRENT form amounts on top
 */
function calculateYTD() {
  if (!selectedEmployee.value || !form.payDate) return

  const currentPayDate = toDate(form.payDate)
  if (!currentPayDate) return

  const currentYear = currentPayDate.getFullYear()
  const empDocId = selectedEmployee.value.id
  const empNumber = selectedEmployee.value.employeeId

  const priorStubs = (payStubs.value || []).filter(stub => {
    const stubPayDate = toDate(stub.payDate)
    if (!stubPayDate) return false

    const sameEmployee =
      stub.employeeId === empDocId ||
      stub.employeeId === empNumber ||
      stub.employeeNumber === empNumber

    return sameEmployee && stubPayDate.getFullYear() === currentYear && stubPayDate.getTime() < currentPayDate.getTime()
  })

  const prior = priorStubs.reduce(
    (acc, s) => {
      acc.gross += Number(s.grossWages || 0)
      acc.net += Number(s.netWages || 0)
      acc.fed += Number(s.federalIncome || 0)
      acc.ss += Number(s.socialSecurity || 0)
      acc.mc += Number(s.medicare || 0)
      acc.rent += Number(s.rent || 0)
      return acc
    },
    { gross: 0, net: 0, fed: 0, ss: 0, mc: 0, rent: 0 }
  )

  form.ytdGrossWages = round2(prior.gross + Number(form.grossWages || 0))
  form.ytdNetWages = round2(prior.net + Number(form.netWages || 0))
  form.ytdFederalIncome = round2(prior.fed + Number(form.federalIncome || 0))
  form.ytdSocialSecurity = round2(prior.ss + Number(form.socialSecurity || 0))
  form.ytdMedicare = round2(prior.mc + Number(form.medicare || 0))
  form.ytdRent = round2(prior.rent + Number(form.rent || 0))
}

function clearForm() {
  selectedEmployee.value = null
  selectedInvoice.value = null
  employeeSearchTerm.value = ''
  showEmployeeDropdown.value = false
  editingPayStubId.value = null
  clearFormData()
}

function clearFormData() {
  form.payPeriodStart = ''
  form.payPeriodEnd = ''
  form.payDate = todayISO()
  form.invoiceId = ''
  selectedInvoice.value = null
  invoiceSearchTerm.value = ''
  form.memo = ''
  form.grossWages = 0
  form.totalHours = 0
  form.hourlyRate = 0
  form.overtimeHours = 0
  form.overtimeRate = 0
  form.rent = 0
  form.federalIncome = 0
  form.socialSecurity = 0
  form.medicare = 0
  form.refund = 0
  form.otherDeductions = 0
  form.netWages = 0
  form.ytdGrossWages = 0
  form.ytdNetWages = 0
  form.ytdFederalIncome = 0
  form.ytdSocialSecurity = 0
  form.ytdMedicare = 0
  form.ytdRent = 0
}

async function createPayStub() {
  if (!selectedEmployee.value) {
    await Swal.fire({ icon: 'warning', title: 'No Employee Selected', text: 'Please select an employee.', confirmButtonColor: '#2563eb' })
    return
  }

  if (!form.invoiceId || !form.payPeriodStart || !form.payPeriodEnd || !form.payDate || !form.totalHours) {
    await Swal.fire({ icon: 'error', title: 'Missing Required Fields', text: 'Please fill in all required fields including total hours.', confirmButtonColor: '#2563eb' })
    return
  }

  // Ensure YTD is up to date before saving
  calculateYTD()

  try {
    const payload = {
      employeeId: selectedEmployee.value.id, // Firestore doc id
      employeeName: `${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName}`,
      employeeNumber: selectedEmployee.value.employeeId || '', // human employee number
      invoiceId: form.invoiceId,
      invoiceNumber: selectedInvoice.value?.invoiceNumber || '',
      propertyName: selectedInvoice.value?.propertyName || '',
      departmentName: selectedInvoice.value?.departmentName || '',
      payPeriodStart: form.payPeriodStart,
      payPeriodEnd: form.payPeriodEnd,
      payDate: form.payDate,
      memo: form.memo || '',
      grossWages: Number(form.grossWages) || 0,
      totalHours: Number(form.totalHours) || 0,
      hourlyRate: Number(form.hourlyRate) || 0,
      overtimeHours: Number(form.overtimeHours) || 0,
      overtimeRate: Number(form.overtimeRate) || 0,
      rent: Number(form.rent) || 0,
      federalIncome: Number(form.federalIncome) || 0,
      socialSecurity: Number(form.socialSecurity) || 0,
      medicare: Number(form.medicare) || 0,
      refund: Number(form.refund) || 0,
      otherDeductions: Number(form.otherDeductions) || 0,
      netWages: Number(form.netWages) || 0,
      // store computed YTD for recordkeeping
      ytdGrossWages: Number(form.ytdGrossWages) || 0,
      ytdNetWages: Number(form.ytdNetWages) || 0,
      ytdFederalIncome: Number(form.ytdFederalIncome) || 0,
      ytdSocialSecurity: Number(form.ytdSocialSecurity) || 0,
      ytdMedicare: Number(form.ytdMedicare) || 0,
      ytdRent: Number(form.ytdRent) || 0,
      createdAt: serverTimestamp()
    }

    await addDoc(collection(db, 'payStubs'), payload)

    await Swal.fire({ icon: 'success', title: 'Pay Stub Created', text: 'The pay stub has been successfully created.', timer: 1500, showConfirmButton: false })
    clearForm()
  } catch (error) {
    console.error('Error creating pay stub:', error)
    await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create pay stub. Please try again.', confirmButtonColor: '#2563eb' })
  }
}

async function updatePayStub() {
  if (!editingPayStubId.value) return

  // Ensure YTD is up to date before saving
  calculateYTD()

  try {
    const payload = {
      invoiceId: form.invoiceId,
      invoiceNumber: selectedInvoice.value?.invoiceNumber || '',
      propertyName: selectedInvoice.value?.propertyName || '',
      departmentName: selectedInvoice.value?.departmentName || '',
      payPeriodStart: form.payPeriodStart,
      payPeriodEnd: form.payPeriodEnd,
      payDate: form.payDate,
      memo: form.memo || '',
      grossWages: Number(form.grossWages) || 0,
      totalHours: Number(form.totalHours) || 0,
      hourlyRate: Number(form.hourlyRate) || 0,
      overtimeHours: Number(form.overtimeHours) || 0,
      overtimeRate: Number(form.overtimeRate) || 0,
      rent: Number(form.rent) || 0,
      federalIncome: Number(form.federalIncome) || 0,
      socialSecurity: Number(form.socialSecurity) || 0,
      medicare: Number(form.medicare) || 0,
      refund: Number(form.refund) || 0,
      otherDeductions: Number(form.otherDeductions) || 0,
      netWages: Number(form.netWages) || 0,
      ytdGrossWages: Number(form.ytdGrossWages) || 0,
      ytdNetWages: Number(form.ytdNetWages) || 0,
      ytdFederalIncome: Number(form.ytdFederalIncome) || 0,
      ytdSocialSecurity: Number(form.ytdSocialSecurity) || 0,
      ytdMedicare: Number(form.ytdMedicare) || 0,
      ytdRent: Number(form.ytdRent) || 0,
      updatedAt: serverTimestamp()
    }

    await updateDoc(doc(db, 'payStubs', editingPayStubId.value), payload)

    await Swal.fire({ icon: 'success', title: 'Pay Stub Updated', text: 'The pay stub has been successfully updated.', timer: 1500, showConfirmButton: false })
    clearForm()
  } catch (error) {
    console.error('Error updating pay stub:', error)
    await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update pay stub. Please try again.', confirmButtonColor: '#2563eb' })
  }
}

async function deletePayStub(id) {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  })

  if (!result.isConfirmed) return

  try {
    await deleteDoc(doc(db, 'payStubs', id))
    await Swal.fire('Deleted!', 'The pay stub has been deleted.', 'success')
  } catch (error) {
    console.error('Error deleting pay stub:', error)
    await Swal.fire('Error!', 'Failed to delete pay stub.', 'error')
  }
}

async function archivePayStub(id) {
  if (!id) return
  const confirmed = await Swal.fire({
    title: 'Archive Pay Stub?',
    text: 'This pay stub will be moved to archives.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Archive',
    cancelButtonText: 'Cancel'
  })

  if (confirmed.isConfirmed) {
    try {
      await updateDoc(doc(db, 'payStubs', id), { archived: true })
      Swal.fire('Archived!', 'Pay stub has been archived.', 'success')
    } catch (e) {
      console.error('Error archiving pay stub:', e)
      Swal.fire('Error', 'Failed to archive pay stub.', 'error')
    }
  }
}

async function unarchivePayStub(id) {
  if (!id) return
  const confirmed = await Swal.fire({
    title: 'Unarchive Pay Stub?',
    text: 'This pay stub will be restored to active pay stubs.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Unarchive',
    cancelButtonText: 'Cancel'
  })

  if (confirmed.isConfirmed) {
    try {
      await updateDoc(doc(db, 'payStubs', id), { archived: false })
      Swal.fire('Restored!', 'Pay stub has been restored.', 'success')
    } catch (e) {
      console.error('Error unarchiving pay stub:', e)
      Swal.fire('Error', 'Failed to restore pay stub.', 'error')
    }
  }
}

function togglePayStubSelection(payStubId) {
  const idx = selectedPayStubs.value.indexOf(payStubId)
  if (idx > -1) {
    selectedPayStubs.value.splice(idx, 1)
  } else {
    selectedPayStubs.value.push(payStubId)
  }
}

function toggleSelectAll() {
  if (allPayStubsSelected.value) {
    selectedPayStubs.value = []
  } else {
    selectedPayStubs.value = pagedPayStubs.value.map(ps => ps.id)
  }
}

function clearSelection() {
  selectedPayStubs.value = []
}

async function bulkArchivePayStubs() {
  if (selectedPayStubs.value.length === 0) return

  const result = await Swal.fire({
    title: `Archive ${selectedPayStubs.value.length} Pay Stub(s)?`,
    html: `<p>These pay stubs will be moved to archives.</p>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive All',
    cancelButtonText: 'Cancel'
  })

  if (result.isConfirmed) {
    Swal.fire({
      title: 'Archiving...',
      html: '<div class="custom-loader"></div>',
      allowOutsideClick: false,
      showConfirmButton: false
    })

    try {
      const updates = selectedPayStubs.value.map(id =>
        updateDoc(doc(db, 'payStubs', id), { archived: true })
      )
      await Promise.all(updates)

      clearSelection()
      Swal.fire('Success!', `${selectedPayStubs.value.length} pay stub(s) archived.`, 'success')
    } catch (e) {
      console.error('Error archiving pay stubs:', e)
      Swal.fire('Error', 'Failed to archive some pay stubs.', 'error')
    }
  }
}

function viewPayStub(payStub) {
  const employee = employees.value.find(e => e.id === payStub.employeeId)
  if (employee) selectEmployee(employee)

  editingPayStubId.value = payStub.id
  form.invoiceId = payStub.invoiceId
  onInvoiceChange()

  form.payPeriodStart = payStub.payPeriodStart
  form.payPeriodEnd = payStub.payPeriodEnd
  form.payDate = payStub.payDate
  form.memo = payStub.memo || ''
  form.grossWages = payStub.grossWages
  form.totalHours = payStub.totalHours
  form.hourlyRate = payStub.hourlyRate
  form.overtimeHours = payStub.overtimeHours || 0
  form.overtimeRate = payStub.overtimeRate || 0
  form.rent = payStub.rent
  form.federalIncome = payStub.federalIncome
  form.socialSecurity = payStub.socialSecurity
  form.medicare = payStub.medicare
  form.refund = payStub.refund
  form.otherDeductions = payStub.otherDeductions
  form.netWages = payStub.netWages

  // Recompute YTD for this record context (don’t trust stored YTD)
  calculateYTD()

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/**
 * ✅ PRINT FIX:
 * Even if older records in Firestore have wrong ytd* fields,
 * we compute YTD LIVE at print time so the PDF is correct.
 */
function computeYTDForPrint(targetStub) {
  const payDate = toDate(targetStub.payDate)
  if (!payDate) {
    return {
      ytdGrossWages: Number(targetStub.ytdGrossWages || 0),
      ytdNetWages: Number(targetStub.ytdNetWages || 0),
      ytdFederalIncome: Number(targetStub.ytdFederalIncome || 0),
      ytdSocialSecurity: Number(targetStub.ytdSocialSecurity || 0),
      ytdMedicare: Number(targetStub.ytdMedicare || 0),
      ytdRent: Number(targetStub.ytdRent || 0),
    }
  }

  const year = payDate.getFullYear()
  const empDocId = targetStub.employeeId
  const empNumber = targetStub.employeeNumber

  const priorStubs = (payStubs.value || []).filter(s => {
    const d = toDate(s.payDate)
    if (!d) return false
    const sameEmployee = s.employeeId === empDocId || s.employeeId === empNumber || s.employeeNumber === empNumber
    return sameEmployee && d.getFullYear() === year && d.getTime() < payDate.getTime()
  })

  const prior = priorStubs.reduce(
    (acc, s) => {
      acc.gross += Number(s.grossWages || 0)
      acc.net += Number(s.netWages || 0)
      acc.fed += Number(s.federalIncome || 0)
      acc.ss += Number(s.socialSecurity || 0)
      acc.mc += Number(s.medicare || 0)
      acc.rent += Number(s.rent || 0)
      return acc
    },
    { gross: 0, net: 0, fed: 0, ss: 0, mc: 0, rent: 0 }
  )

  return {
    ytdGrossWages: round2(prior.gross + Number(targetStub.grossWages || 0)),
    ytdNetWages: round2(prior.net + Number(targetStub.netWages || 0)),
    ytdFederalIncome: round2(prior.fed + Number(targetStub.federalIncome || 0)),
    ytdSocialSecurity: round2(prior.ss + Number(targetStub.socialSecurity || 0)),
    ytdMedicare: round2(prior.mc + Number(targetStub.medicare || 0)),
    ytdRent: round2(prior.rent + Number(targetStub.rent || 0)),
  }
}

function printPayStub(payStub) {
  const employee = employees.value.find(e => e.id === payStub.employeeId)

  const invoice = invoices.value.find(inv => inv.id === payStub.invoiceId)
  const companyName = invoice?.companyName || 'RTH GROUP INC.'
  const companyAddress = invoice?.companyAddress || '1169 Wears Valley Road Pigeon Forge, Tennesse 37863'

  // ✅ compute YTD live for print
  const ytd = computeYTDForPrint(payStub)

  const element = document.createElement('div')
  element.innerHTML = `
    <div style="padding: 12px; font-family: Arial, sans-serif; color: #333; font-size: 13px;">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #2563eb;">
        <div>
          <h1 style="margin: 0; color: #1f2937; font-size: 18px; font-weight: bold;">${companyName}</h1>
          <p style="margin: 2px 0; color: #666; font-size: 11px;">${companyAddress}</p>
          <p style="margin: 2px 0; color: #666; font-size: 11px;">Phone: (865) 352-8700</p>
        </div>
        <div style="text-align: right;">
          <h2 style="margin: 0; color: #2563eb; font-size: 20px; font-weight: bold;">PAY STUB</h2>
          <p style="margin: 2px 0; font-size: 11px;">Pay Date: ${formatDate(payStub.payDate)}</p>
        </div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 8px;">
        <div style="background-color: #f8f9fa; padding: 8px; border-radius: 4px;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold;">EMPLOYEE</h3>
          <p style="margin: 2px 0; font-weight: bold; font-size: 12px;">${payStub.employeeName}</p>
          <p style="margin: 2px 0; font-size: 11px;">ID: ${payStub.employeeNumber}</p>
          ${employee?.usAddress ? `
            <p style="margin: 6px 0 0 0; padding-top: 6px; border-top: 1px solid #ddd; font-size: 10px; line-height: 1.2;">
              <strong>Address:</strong> ${employee.usAddress}, ${employee.usCity}, ${employee.usState} ${employee.usZipCode}, ${employee.usCountry}
            </p>
          ` : ''}
        </div>
        <div style="background-color: #f8f9fa; padding: 8px; border-radius: 4px;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold;">PAY PERIOD</h3>
          <p style="margin: 2px 0; font-size: 11px;">${formatDateRange(payStub.payPeriodStart, payStub.payPeriodEnd)}</p>
          <p style="margin: 4px 0 0 0; font-size: 11px;"><strong>Property:</strong> ${payStub.propertyName}</p>
          ${payStub.memo ? `
            <p style="margin: 6px 0 0 0; padding-top: 6px; border-top: 1px solid #ddd; font-size: 10px;"><strong>Memo:</strong> ${payStub.memo}</p>
          ` : ''}
        </div>
      </div>

      <div style="margin-bottom: 6px;">
        <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">EARNINGS</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #2563eb; color: white;">
              <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">RATE</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">HOURS</th>
              <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Regular Pay</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.hourlyRate)}</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${Number(payStub.totalHours).toFixed(2)}</td>
              <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency((payStub.hourlyRate || 0) * (payStub.totalHours || 0))}</td>
            </tr>
            ${payStub.overtimeHours > 0 ? `
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Overtime Pay</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.overtimeRate)}</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${Number(payStub.overtimeHours).toFixed(2)}</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency((payStub.overtimeRate || 0) * (payStub.overtimeHours || 0))}</td>
              </tr>
            ` : ''}
            ${payStub.refund > 0 ? `
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Refund / Reimbursement</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">-</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">-</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.refund)}</td>
              </tr>
            ` : ''}
          </tbody>
          <tfoot>
            <tr style="background-color: #f8f9fa; font-weight: bold;">
              <td colspan="3" style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">Total Earnings</td>
              <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">${currency(payStub.grossWages + (payStub.refund || 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style="display: flex; gap: 10px; margin-bottom: 6px;">
        <div style="flex: 1;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">DEDUCTIONS</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #2563eb; color: white;">
                <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
                <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${payStub.federalIncome > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Federal Income Tax</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.federalIncome)}</td>
                </tr>
              ` : ''}
              ${payStub.socialSecurity > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Social Security</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.socialSecurity)}</td>
                </tr>
              ` : ''}
              ${payStub.medicare > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Medicare</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.medicare)}</td>
                </tr>
              ` : ''}
              ${payStub.rent > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Rent</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.rent)}</td>
                </tr>
              ` : ''}
              ${payStub.otherDeductions > 0 ? `
                <tr>
                  <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Other Deductions</td>
                  <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(payStub.otherDeductions)}</td>
                </tr>
              ` : ''}
            </tbody>
            <tfoot>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">Total Deductions</td>
                <td style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">${currency((payStub.grossWages + (payStub.refund || 0)) - payStub.netWages)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style="flex: 1;">
          <h3 style="margin: 0 0 4px 0; color: #1f2937; font-size: 12px; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #2563eb; padding-bottom: 2px;">YEAR TO DATE (YTD)</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #2563eb; color: white;">
                <th style="padding: 3px; text-align: left; border: 1px solid #ddd; font-size: 11px;">DESCRIPTION</th>
                <th style="padding: 3px; text-align: right; border: 1px solid #ddd; font-size: 11px;">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Gross Wages</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(ytd.ytdGrossWages || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Federal Income Tax</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(ytd.ytdFederalIncome || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Social Security</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(ytd.ytdSocialSecurity || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Medicare</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(ytd.ytdMedicare || 0)}</td>
              </tr>
              <tr>
                <td style="padding: 3px; border: 1px solid #eee; font-size: 11px;">Rent</td>
                <td style="padding: 3px; border: 1px solid #eee; text-align: right; font-size: 11px;">${currency(ytd.ytdRent || 0)}</td>
              </tr>
              <tr style="background-color: #f8f9fa; font-weight: bold;">
                <td style="padding: 3px; border: 1px solid #ddd; font-size: 11px;">Net Wages</td>
                <td style="padding: 3px; border: 1px solid #ddd; text-align: right; font-size: 11px;">${currency(ytd.ytdNetWages || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; margin-top: 6px;">
        <div style="border: 2px solid #000; padding: 6px 16px; text-align: center; background: #f9f9f9;">
          <h3 style="margin: 0 0 3px 0; text-transform: uppercase; font-size: 11px; color: #1f2937; font-weight: bold;">NET PAY</h3>
          <div style="font-size: 19px; font-weight: bold; color: #1f2937;">${currency(payStub.netWages)}</div>
        </div>
      </div>
    </div>
  `

  const opt = {
    margin: 10,
    filename: `PayStub_${payStub.employeeNumber}_${payStub.payDate}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
  }

  html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
    const pdfUrl = pdf.output('bloburi')
    const printWindow = window.open(pdfUrl)
    printWindow.onload = () => printWindow.print()
  })
}

/** ---------- Utils ---------- */
function todayISO() {
  return new Date().toISOString().split('T')[0]
}

function currency(n) {
  try {
    return (Number.isFinite(n) ? n : 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
  } catch (e) {
    console.error('Error formatting currency:', e)
    return '$0.00'
  }
}

function formatDate(val) {
  try {
    if (!val) return '—'
    
    // Handle Firestore timestamps
    if (val?.seconds) {
      const d = new Date(val.seconds * 1000)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    }
    
    // Handle date strings (YYYY-MM-DD format from HTML date input)
    // Split and parse manually to avoid timezone conversion
    if (typeof val === 'string' && val.includes('-')) {
      const [year, month, day] = val.split('-')
      return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
    
    // Handle date objects
    const d = new Date(val)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch (e) {
    console.error('Error formatting date:', e)
    return '—'
  }
}

function formatDateRange(start, end) {
  try {
    if (!start || !end) return '—'
    
    // Parse dates, handling both string and object formats
    let startDate, endDate
    
    if (typeof start === 'string' && start.includes('-')) {
      const [year, month, day] = start.split('-')
      startDate = new Date(Number(year), Number(month) - 1, Number(day))
    } else if (start?.seconds) {
      startDate = new Date(start.seconds * 1000)
    } else {
      startDate = new Date(start)
    }
    
    if (typeof end === 'string' && end.includes('-')) {
      const [year, month, day] = end.split('-')
      endDate = new Date(Number(year), Number(month) - 1, Number(day))
    } else if (end?.seconds) {
      endDate = new Date(end.seconds * 1000)
    } else {
      endDate = new Date(end)
    }
    
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  } catch (e) {
    console.error('Error formatting date range:', e)
    return '—'
  }
}
</script>


<style scoped>
.overview-header h1 {
  font-size: 24px;
  color: #1f2937;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
}

.form-section h2,
.form-section h3 {
  margin-bottom: 16px;
  font-size: 20px;
  color: #111827;
}

.form-subsection {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.form-subsection:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.form-subsection h3 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #374151;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.form-group label {
  margin-bottom: 4px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.form-group input,
.form-group select {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.readonly-field {
  background: #f3f4f6 !important;
  color: #6b7280;
  cursor: not-allowed;
}

.grid {
  display: grid;
  gap: 12px;
}

.grid-2 {
  grid-template-columns: 1fr 1fr;
}

.grid-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

@media (max-width: 820px) {

  .grid-2,
  .grid-3 {
    grid-template-columns: 1fr;
  }
}

/* Employee Search Styles */
.employee-search-wrapper {
  position: relative;
}

.employee-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.employee-option {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.employee-option:hover {
  background-color: #f3f4f6;
}

.employee-option:last-child {
  border-bottom: none;
}

.employee-name {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.employee-details {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.no-employees {
  padding: 8px 12px;
  color: #6b7280;
  font-style: italic;
  font-size: 12px;
  text-align: center;
}

/* Invoice Details */
.invoice-details {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.invoice-details h4 {
  margin: 0 0 8px 0;
  background: #fef3c7;
  border-radius: 4px;
  text-align: center;
  color: #92400e;
}

/* Summary Section */
.summary-section {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
}

.summary-grid {
  display: grid;
  gap: 8px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item.final-pay {
  background: #10b981;
  color: white;
  padding: 12px;
  margin: 8px -16px -16px;
  border-radius: 0 0 6px 6px;
  font-weight: 600;
  font-size: 16px;
}

.summary-label {
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
}

/* Table Styles */
.table-wrap {
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

th,
td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

tr:last-child td {
  border-bottom: none;
}

.num {
  font-family: 'Courier New', Courier, monospace;
  font-weight: 600;
}

.action-button {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: none;
  margin-right: 8px;
  transition: background-color 0.2s;
}

.action-button:last-child {
  margin-right: 0;
}

.action-button.view {
  background: #e0f2fe;
  color: #0369a1;
}

.action-button.view:hover {
  background: #bae6fd;
}

.action-button.delete {
  background: #fee2e2;
  color: #b91c1c;
}

.action-button.delete:hover {
  background: #fecaca;
}

.action-button.archive {
  background: #fef3c7;
  color: #92400e;
}

.action-button.archive:hover {
  background: #fde68a;
}

.action-button.unarchive {
  background: #dbeafe;
  color: #1e40af;
}

.action-button.unarchive:hover {
  background: #bfdbfe;
}

.invoice-table-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.search-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1e3a8a;
}

.search-scope {
  margin-left: auto;
  font-size: 0.85rem;
  color: #64748b;
}

th.sortable {
  cursor: pointer;
  user-select: none;
}

th.sortable:hover {
  color: #2563eb;
}

.employee-number {
  font-size: 0.8rem;
  color: #6b7280;
}

.archived-tag,
.paid-tag {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
}

.archived-tag {
  background: #f3f4f6;
  color: #4b5563;
}

.paid-tag {
  margin-left: 6px;
  background: #dcfce7;
  color: #166534;
}

.employee-option.highlighted {
  background: #eff6ff;
}

.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  color: #374151;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-controls button,
.pagination-controls select {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.year-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #374151;
}

.year-filter select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}

.table-search {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1 1 280px;
}

.table-search .search-icon {
  position: absolute;
  left: 10px;
  color: #9ca3af;
  pointer-events: none;
}

.table-search input {
  flex: 1;
  min-width: 0;
  padding: 6px 10px 6px 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.archive-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.archive-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.archive-toggle input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.bulk-actions {
  background: #f3f4f6;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selection-count {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.bulk-archive-button {
  background: #fbbf24;
  color: #78350f;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-right: 8px;
}

.bulk-archive-button:hover {
  background: #f59e0b;
}

.bulk-cancel-button {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.bulk-cancel-button:hover {
  background: #f9fafb;
}

.no-data {
  text-align: center;
  padding: 24px;
  color: #6b7280;
  font-style: italic;
}

/* Tax Input Group */
.tax-input-group {
  display: flex;
  gap: 8px;
}

.tax-mode-select {
  flex: 1;
  min-width: 140px;
}

.tax-amount-display {
  flex: 1;
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.save-button {
  background: #2563eb;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-button:hover {
  background: #1d4ed8;
}

.save-button:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.cancel-button {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover {
  background: #f3f4f6;
}

.action-button.print {
  background: #6366f1;
  color: white;
}

.action-button.print:hover {
  background: #4f46e5;
}
</style>
